import { LabeledValue } from "../../MainSearch/Types";
import {
  CaseSubCriterias,
  CreditorSubCriterias,
  DebtorSubCriterias,
  InvoiceSubCriterias,
  MainCriterias,
  COLEN,
} from "constants/globalsearch";
import {
  getCaseSubCriterias,
  getCreditorSubCriterias,
  getDebtorSubCriterias,
  getInvoiceSubCategories,
  getMainSearchCriterias,
  getInitialDropDownOptionValues,
} from "us.common/functions/MainSearch";
import { ICombinedEnums, ISearchBar } from "../Interfaces";
import { SearchHistoryResult } from "reducers/GlobalSearch/Types";
import { i18n } from "us.helper";

/**
 * @function
 * @description Sort history dropdown by date
 * @param firstHisObj first history object
 * @param secondHisObj second history object
 * @returns the difference of the time in number type
 */
export const sortDescendFromDate = (
  firstHisObj: SearchHistoryResult,
  secondHisObj: SearchHistoryResult,
): number => {
  try {
    return (
      new Date(secondHisObj.timestamp).getTime() -
      new Date(firstHisObj.timestamp).getTime()
    );
  } catch (e) {
    return 0;
  }
};

/**
 * @function
 * @description Called to get the list for Autocomplete dropdown subcategories
 * @param mainCategoryValue the main critria selected
 * @returns the matching list of sub criterias dropdown
 */
export const getMatchingSubCriterias = (
  mainCategoryValue: MainCriterias,
):
  | LabeledValue<InvoiceSubCriterias>[]
  | LabeledValue<CaseSubCriterias>[]
  | LabeledValue<CreditorSubCriterias>[]
  | LabeledValue<DebtorSubCriterias>[] => {
  switch (mainCategoryValue) {
    case MainCriterias.INVOICE:
      return getInvoiceSubCategories();
    case MainCriterias.CASE:
      return getCaseSubCriterias();
    case MainCriterias.CREDITOR:
      return getCreditorSubCriterias();
    case MainCriterias.DEBTOR:
      return getDebtorSubCriterias();
    default:
      return [];
  }
};
/**
 * @function
 * @description It takes a sub criteria and update the value if it has a transerlate key and returns that sub criteria 
 * @param invoiceSubCriterias - invoiceSubCriterias
 * @returns sub criteria 
 */
export const transerlateMatchingSubEnum = (invoiceSubCriterias: any) => {
  try {
    const transerlationKeyExpression = /^[A-Z0-9_]+$/;
    for (const key in invoiceSubCriterias) {
      if (transerlationKeyExpression.test(invoiceSubCriterias[key])) {
        invoiceSubCriterias[key] = i18n.t(`US.COLLECTION.COMMON:COMMON.${invoiceSubCriterias[key]}`);
      }
    }
    return invoiceSubCriterias;
  } catch (e) {
    return invoiceSubCriterias;
  }
};

/**
 * @function
 * @description It takes a main criteria and returns the matching sub criteria
 * @param {MainCriterias} mainCriteria - MainCriterias
 * @returns Matching enum.
 */
export const getMatchingSubEnum = (
  mainCriteria: MainCriterias,
): ICombinedEnums => {
  switch (mainCriteria) {
    case MainCriterias.INVOICE:
      return transerlateMatchingSubEnum(InvoiceSubCriterias);
    case MainCriterias.CASE:
      return transerlateMatchingSubEnum(CaseSubCriterias);
    case MainCriterias.CREDITOR:
      return CreditorSubCriterias;
    case MainCriterias.DEBTOR:
      return DebtorSubCriterias;
    default:
      return CaseSubCriterias;
  }
};

/**
 * @function
 * @description Get the regular expression prepared for validating input
 * @param mainCriteria type of MainCriteriaEnum
 * @param subCriteria one of the SubTypeEnums
 * @returns regularExpression object
 */
export const getSearchRegExps = (
  mainCriteria: string,
  subCriteria: string,
): { [key: string]: RegExp } => {
  return {
    allSpaces: new RegExp("\\s+", "g"),
    inputWithMainCriteria: new RegExp(`^\\s*${mainCriteria + COLEN}`),
    inputWithSubCriteria: new RegExp(
      `^\\s*${mainCriteria + COLEN}\\s*${subCriteria + COLEN}`,
    ),
  };
};

/**
 * @function
 * @description parse timestamp string to milliseconds
 * @param timestamp - History timestamp string
 * @returns
 */
const parseTimestamp = (timestamp?: string | null): number => {
  try {
    if (!timestamp) return Number.NEGATIVE_INFINITY;
    const time = Date.parse(timestamp);
    return isNaN(time) ? Number.NEGATIVE_INFINITY : time;
  } catch (e) {
    return Number.NEGATIVE_INFINITY;
  }
};

/**
 * @function
 * @description sort two SearchHistoryResult by latest timestamp
 * @param a - first SearchHistoryResult
 * @param b - second SearchHistoryResult
 * @returns
 */
const sortByLatestTimestamp = (
  a: SearchHistoryResult,
  b: SearchHistoryResult,
): number => {
  try {
    if (!a && !b) return 0;
    // goes last
    if (!a) return 1;
    if (!b) return -1;

    return parseTimestamp(b.timestamp) - parseTimestamp(a.timestamp);
  } catch {
    return 0;
  }
};

/**
 * @function
 * @description creating the initial autocomplete drop-down
 * @returns search bar dropdown
 */
export const getFormikSearchBarValues = (
  searchHistory: SearchHistoryResult[],
  caseNo: string | number,
  GetHistoryUI: any,
  historyBar: any,
): ISearchBar => {
  try {
    // for initial formik values
    if (searchHistory && searchHistory.length > 0) {
      let historyOptions = [];
      if (caseNo) {
        historyOptions = [
          ...searchHistory
            .filter((x: any) => x.caseId !== caseNo)
            .map((item: SearchHistoryResult) => {
              return GetHistoryUI(item.caseNo, item.debtorName);
            })
            .slice(0, 5),
        ];
      } else {
        historyOptions = [
          ...searchHistory
            .sort(sortByLatestTimestamp)
            .map((item: SearchHistoryResult) => {
              return GetHistoryUI(item.caseNo, item.debtorName);
            })
            .slice(0, 5),
        ];
      }

      return {
        options: getInitialDropDownOptionValues(
          undefined,
          getMainSearchCriterias(),
          "",
        ).concat({
          label: historyBar,
          key: "hisotoryBar",
          value: "hisotoryBar",
          options: historyOptions,
        }),
        value: "",
      };
    } else {
      return {
        options: getInitialDropDownOptionValues(
          undefined,
          getMainSearchCriterias(),
          "",
        ),
        value: "",
      };
    }
  } catch (e) {
    return { options: [], value: "" };
  }
};
